import { ALL_WORKSHOPS_QUERY } from '@/modules/vehicles/queries'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo, useState } from 'react'
import type { ComboboxCommonProps, ComboboxItem } from './combobox'
import { Combobox } from './combobox'

interface CommonProps
  extends Omit<ComboboxCommonProps<string>, 'onCreate' | 'createLabel'> {
  clearable?: boolean
  /**
   * Adds a missing workshop from the search text and resolves to its id. Build
   * it with `useCreateWorkshopByName` outside the prompt this picker sits in.
   */
  onCreateWorkshop?(name: string): Promise<string | null>
}

interface SingleProps extends CommonProps {
  mode: 'single'
  value: string
  onChange(value: string): void
}

interface MultiProps extends CommonProps {
  mode: 'multiple'
  value: string[]
  onChange(value: string[]): void
}

type Props = SingleProps | MultiProps

export function WorkshopCombobox({
  mode,
  value,

  disabled = false,
  placeholder,
  searchPlaceholder,
  noResultsText,
  clearable = true,
  onCreateWorkshop,

  onChange,

  ...props
}: Props) {
  const translations = useTranslate({
    loading: 'common.loading',
    single: {
      placeholder: 'components.combobox.workshop.placeholder',
      searchPlaceholder: 'components.combobox.workshop.search-placeholder',
      noResults: 'components.combobox.workshop.no-results',
    },
    multiple: {
      placeholder: 'components.multi-combobox.workshop.placeholder',
      searchPlaceholder:
        'components.multi-combobox.workshop.search-placeholder',
      noResults: 'components.combobox.workshop.no-results',
    },
    create: ['components.combobox.workshop.create', { name: '' }],
  })

  const [creating, setCreating] = useState(false)

  const { data, loading } = useQuery(ALL_WORKSHOPS_QUERY, {
    variables: { orderBy: 'name' },
  })

  const items = useMemo<ComboboxItem<string>[]>(
    () =>
      (data?.allWorkshops.edges ?? []).flatMap(edge =>
        edge?.node ? [{ value: edge.node.id, label: edge.node.name }] : []
      ),
    [data?.allWorkshops.edges]
  )

  const resolvedPlaceholder = loading
    ? translations.loading
    : placeholder ?? translations[mode].placeholder

  if (mode === 'multiple') {
    return (
      <Combobox
        {...props}
        mode="multiple"
        value={value}
        items={items}
        disabled={disabled}
        placeholder={resolvedPlaceholder}
        searchPlaceholder={
          searchPlaceholder ?? translations.multiple.searchPlaceholder
        }
        noResultsText={noResultsText ?? translations.multiple.noResults}
        onChange={onChange}
      />
    )
  }

  const handleCreate = async (
    create: (name: string) => Promise<string | null>,
    name: string
  ) => {
    setCreating(true)
    try {
      const id = await create(name)
      if (id) onChange(id)
    } finally {
      setCreating(false)
    }
  }

  return (
    <Combobox
      {...props}
      mode="single"
      value={value}
      items={items}
      disabled={disabled || creating}
      clearable={clearable}
      placeholder={resolvedPlaceholder}
      searchPlaceholder={
        searchPlaceholder ?? translations.single.searchPlaceholder
      }
      noResultsText={noResultsText ?? translations.single.noResults}
      createLabel={name => translations.create({ name })}
      onCreate={
        onCreateWorkshop
          ? name => void handleCreate(onCreateWorkshop, name)
          : undefined
      }
      onChange={onChange}
    />
  )
}
